import orderBy from 'lodash.orderby'
import {makeAutoObservable, observable} from 'mobx'
import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useContext, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import SortIcon from './sort.svg'
import SortDownIcon from './sort-down.svg'
import SortDownLargeIcon from './sort-down-large.svg'
import SortLargeIcon from './sort-large.svg'
import SortUpIcon from './sort-up.svg'
import SortUpLargeIcon from './sort-up-large.svg'

const largeArrows = {
  none: SortLargeIcon,
  up: SortUpLargeIcon,
  down: SortDownLargeIcon
}
const smallArrows = {none: SortIcon, up: SortUpIcon, down: SortDownIcon}

export interface TableColumn<T> {
  title: React.ReactNode
  sortBy?: (x: T) => string
  width?: string | number
  render: (x: T) => React.ReactNode
  className?: string
}

export interface Sort {
  columnIndex: number
  order: boolean
}

export interface TableProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  rows: ({
    data: T
  } & React.HTMLAttributes<HTMLDivElement>)[]
  columns: TableColumn<T>[]
  defaultSort?: Sort
  large?: boolean
  underHeadElement?: React.ReactNode
  noScroll?: boolean
  noPaddings?: boolean
}

interface State<T> {
  sort?: Sort
  rows: ({
    data: T
  } & React.HTMLAttributes<HTMLDivElement>)[]
  columns: TableColumn<T>[]
}

class Store<T> {
  state: State<T> = observable(
    {
      rows: [],
      columns: []
    },
    {}
  )

  constructor(
    defaultSort: Sort | undefined,
    rows: TableProps<T>['rows'],
    columns: TableProps<T>['columns']
  ) {
    makeAutoObservable(this, {
      state: observable.shallow
    })
    if (defaultSort) {
      this.state.sort = defaultSort
      this.updateFromProps(rows, columns)
    }
  }

  updateFromProps(
    rows: TableProps<T>['rows'],
    columns: TableProps<T>['columns']
  ) {
    this.state.rows = rows
    this.state.columns = columns
  }

  get rows() {
    if (!this.state.sort) {
      return this.state.rows
    }
    const column = this.state.columns[this.state.sort.columnIndex]
    const sortBy = column?.sortBy
    if (!sortBy) {
      return this.state.rows
    }
    return orderBy(
      this.state.rows,
      y => sortBy(y.data).toLowerCase(),
      this.state.sort.order ? 'desc' : 'asc'
    )
  }

  setColumnSort(columnIndex: number) {
    const column = this.state.columns[columnIndex]
    if (!column.sortBy) {
      return
    }
    const sort = this.state.sort
    this.state.sort = {
      columnIndex,
      order: sort?.columnIndex === columnIndex ? !sort?.order : false
    }
  }
}

const StoreContext = React.createContext(undefined as unknown as Store<unknown>)

const useStore = <T,>() => {
  const store = useContext(StoreContext) as Store<T>
  return [store.state, store] as const
}

const TwTableHead = tw.div<{$noPaddings?: boolean}>`
    leading-none
    flex
    h-20
    items-center
    text-third-old
    top-0
    bg-main-old
    ${p => (p.$noPaddings ? '' : 'px-4')}
`

const TwTableRow = tw.div<{$noPaddings?: boolean}>`
    leading-none
    overflow-hidden
    odd:bg-main-even-old
    flex
    h-32
    text-secondary-old
    items-center
    ${p => (p.$noPaddings ? '' : 'px-4')}
`
const HeadShadow = () => {
  const shadow =
    'radial-gradient(at top, var(--color-border-main-old), rgba(0, 0, 0, 0) 70%) 0 0/100% 8px no-repeat'
  return (
    <div
      style={{background: shadow}}
      className="absolute left-0 right-0 h-16 -bottom-16"
    ></div>
  )
}

interface TableHeadProps {
  onTop: boolean
  large?: boolean
  noPaddings?: boolean
}

const TableHead = observer(<T,>(props: TableHeadProps) => {
  const [state, store] = useStore<T>()
  const arrows = props.large ? largeArrows : smallArrows
  return (
    <div className="sticky top-0 z-10">
      <TwTableHead
        className={cn(props.large ? 'text-12' : 'text-11 font-semibold')}
        $noPaddings={props.noPaddings}
      >
        {state.columns.map((x, i) => {
          const Arrow =
            // eslint-disable-next-line no-nested-ternary
            state.sort && state.sort.columnIndex === i
              ? state.sort.order
                ? arrows.down
                : arrows.up
              : arrows.none
          return (
            <div
              key={i}
              style={{width: x.width ?? 0}}
              onClick={() => store.setColumnSort(i)}
              className={cn(
                x.className,
                'px-4 flex items-center',
                x.sortBy && 'cursor-pointer',
                x.width !== undefined ? 'grow-0 shrink-0' : 'shrink-0 flex-grow'
              )}
            >
              {x.title} {x.sortBy && Arrow && <Arrow className="ml-4" />}
            </div>
          )
        })}
      </TwTableHead>
      {!props.onTop && <HeadShadow />}
    </div>
  )
})

interface TableBodyProps {
  large?: boolean
  noPaddings?: boolean
}

const TableBody = observer(<T,>(props: TableBodyProps) => {
  const [state, store] = useStore<T>()
  return (
    <>
      {store.rows.map(({data, ...rest}, i) => (
        <TwTableRow
          data-test="employee-sticker-field-list"
          className={cn(props.large ? 'text-14' : 'text-12')}
          key={i}
          $noPaddings={props.noPaddings}
          {...rest}
        >
          {state.columns.map((x, i) => (
            <div
              key={i}
              style={{width: x.width ?? 0}}
              className={cn(
                x.className,
                'px-4 overflow-hidden',
                x.width === undefined && 'shrink-0 flex-grow'
              )}
            >
              {x.render(data)}
            </div>
          ))}
        </TwTableRow>
      ))}
    </>
  )
})

export const Table = observer(
  <T,>({
    rows,
    columns,
    large,
    defaultSort,
    underHeadElement,
    noScroll = false,
    noPaddings = false,
    ...rest
  }: TableProps<T>) => {
    const store = useMemoOne(() => new Store<T>(defaultSort, rows, columns), [])
    useEffect(() => store.updateFromProps(rows, columns), [rows, columns])
    const [onTop, setOnTop] = useState(true)
    const scrollRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({top: 0})
      }
    }, [rows.length])

    const content = (
      <>
        <TableHead<T> onTop={onTop} large={large} noPaddings={noPaddings} />
        {underHeadElement}
        <TableBody<T> large={large} noPaddings={noPaddings} />
      </>
    )

    if (noScroll) {
      return (
        <StoreContext.Provider value={store}>{content}</StoreContext.Provider>
      )
    }

    return (
      <StoreContext.Provider value={store}>
        <WithPerfectScrollbar
          containerRef={ref => (scrollRef.current = ref)}
          onScrollY={x => {
            setOnTop(x.scrollTop === 0)
          }}
          className={rest.className}
        >
          {content}
        </WithPerfectScrollbar>
      </StoreContext.Provider>
    )
  }
)
